import { useEffect, useState } from "react";

const IST = (ts) =>
  new Date(ts * 1000).toLocaleTimeString("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

/* "Opens in X mins (HH:MM IST)" / "Closes in X mins (HH:MM IST)" from MT5's own session edges. */
const sessionNote = (tick, nowMs) => {
  if (!tick) return null;
  const now = nowMs / 1000;
  const closed = tick.market_open === false;
  const edge = closed ? tick.opens_at : tick.closes_at;
  if (!edge) return null;
  const left = edge - now;
  if (left <= 0) return null;
  const limit = closed ? 3600 : 900;
  if (left > limit) return null;
  const mins = Math.max(1, Math.ceil(left / 60));
  const unit = mins === 1 ? "min" : "mins";
  return `${closed ? "Opens" : "Closes"} in ${mins} ${unit} (${IST(edge)} IST)`;
};

const Cell = ({ label, value, tone, testid }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-400 font-semibold">{label}</p>
    <p
      className={`font-mono text-sm sm:text-lg leading-tight truncate tabular-nums ${tone}`}
      data-testid={testid}
    >
      {value ?? "—"}
    </p>
  </div>
);

/* Live bid/ask pushed over the desk WebSocket — no polling. */
export const TickStrip = ({ symbol, tick, live = true }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!symbol) return null;
  const closed = tick?.market_open === false;
  const note = sessionNote(tick, now);

  return (
    <div
      data-testid="tick-strip"
      className={`border rounded-sm px-3 py-3 sm:px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6 ${
        closed ? "border-amber-900/70 bg-amber-500/5" : "border-zinc-800 bg-black/60"
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 sm:flex-col sm:items-start sm:gap-0">
        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-400 font-semibold truncate">
          {symbol}
        </p>
        {note && (
          <p
            className={`font-mono text-[10px] sm:order-3 sm:mt-0.5 ${
              closed ? "text-amber-300" : "text-sky-300"
            }`}
            data-testid="session-countdown"
          >
            {note}
          </p>
        )}
        {closed ? (
          <p
            className="font-mono text-[10px] text-amber-300 sm:mt-0.5"
            data-testid="market-closed-label"
          >
            market closed
          </p>
        ) : (
          <p className="font-mono text-[10px] text-zinc-400 sm:mt-0.5">
            {live ? "live · streaming" : "reconnecting…"}
          </p>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 sm:flex-1 sm:text-right">
        <Cell label="Bid" value={tick?.bid} tone="text-red-300" testid="tick-bid" />
        <Cell label="Ask" value={tick?.ask} tone="text-emerald-300" testid="tick-ask" />
        <Cell label="Spread" value={tick?.spread} tone="text-zinc-100" testid="tick-spread" />
      </div>
    </div>
  );
};
